


















































































































import Vue from 'vue';
import { EProductVariantType } from '@ringana/configs';
import { ErrorCodes } from '@ringana/errors';
import { IShoppingCartIndividual } from '@/common/interfaces/cart.interface';
import { IIcons, IStyleObject } from '@/common/interfaces/index.interface';
import { ILimitingObject } from '@/common/interfaces/limiting.interface';
import { IMerchandiseItem } from '@/common/interfaces/merchandise.interface';
import { EErrorMessage } from '@/common/enums/messages.enum';
import {
  IProduct,
  IProductDetails,
  IProductVariant,
  ITeaserDot,
  IUnitPriceData,
} from '@/common/interfaces/products.interface';
import { IOldCustomSelectOption } from '@/common/interfaces/shared.interface';
import AddToCartButton from '@/components/layout/buttons/AddToCartButton.vue';
import LimitingProductInfoGrid from '@/components/limiting/product-info/LimitingProductInfoGrid.vue';
import PriceText from '@/components/newsingleproductpage/subcomponents/pricetext/PriceText.vue';
import ProductVariantSelection from '@/components/newsingleproductpage/subcomponents/productvariant/ProductVariantSelection.vue';
import ProductPageSelect from '@/components/newsingleproductpage/subcomponents/select/ProductPageSelect.vue';
import AmountStepper from '@/components/newsingleproductpage/subcomponents/topsection/AmountStepper.vue';
import ApplicationError from '@/errors/application-error';
import FetchError from '@/errors/fetch-error';
import AppHelper from '@/helpers/AppHelper';
import CartHelper from '@/helpers/CartHelper';
import FormatHelper from '@/helpers/FormatHelper';
import ProductHelper from '@/helpers/ProductHelper';
import TIcons from '@/types/TIcons';
import config from '../../../../config';
import ProductPreview from './topsection/ProductPreview.vue';

interface ITopSectionComponent {
  EProductVariantType: typeof EProductVariantType;
  addedToCart: boolean;
  additionalUnitPriceData: string;
  basePriceInfoText: string;
  icons: IIcons;
  loading: boolean;
  productHelper: typeof ProductHelper;
  selectedAmount: number;
}

export default Vue.extend({
  name: `TopSection`,
  components: {
    AddToCartButton,
    AmountStepper,
    LimitingProductInfoGrid,
    PriceText,
    ProductPageSelect,
    ProductPreview,
    ProductVariantSelection,
  },
  data(): ITopSectionComponent {
    return {
      EProductVariantType,
      addedToCart: false,
      additionalUnitPriceData: ``,
      basePriceInfoText: ``,
      icons: TIcons,
      loading: false,
      productHelper: ProductHelper,
      selectedAmount: 1,
    };
  },
  computed: {
    addToCartLabel(): string {
      if(!this.isProductComingSoon && !this.productInfo.soldOut) {
        return ``;
      }
      return this.isProductComingSoon ? this.$t(`products-overview:comingSoon`) : this.$t(`products-overview:soldOut`);
    },
    cartOptions(): IOldCustomSelectOption[] {
      const options: IOldCustomSelectOption[] = [];

      // sort Eigenbedarf to the front to always make sure it is the first cart for the dropdown
      for(const cart of AppHelper.deepClone(this.availableCarts).sort((a, b) => (a.cartTypeId < b.cartTypeId ? -1 : 1))) {
        if(cart.name !== `myOwnCart`) {
          if(!this.productInfo.isMerchandise) { // merchandise can only go to Eigenbedarf - can't be combined with if above because then we'd push the Eigenbedarf cart into the array multiple times
            options.push({
              code: cart.cartId,
              label: `${cart.invoiceAddress.recipient} | ${cart.invoiceAddress.zipCode} ${cart.invoiceAddress.city}`,
            });
          }
        } else {
          options.push({
            code: cart.cartId,
            label: this.$t(`cart:myOwnCart`),
          });
        }
      }

      return options;
    },
    isProductComingSoon(): boolean {
      return this.productInfo.deliveryStatus?.replaceAll(` `, ``).toLowerCase() === `comingsoon`;
    },
    isProductUnavailable(): boolean {
      if(!this.productInfo.deliveryStatusId) {
        return false;
      }

      return config.unavailableDeliveryStatusIds.includes(this.productInfo.deliveryStatusId);
    },
    productInformationLinkStyle(): IStyleObject {
      return {
        borderBottomColor: this.productDetails.informationLinkColor.includes(`#`) ? this.productDetails.informationLinkColor : `#${this.productDetails.informationLinkColor}`,
      };
    },
    productVariantsHeadline(): string {
      if(this.productVariants.length === 0) {
        return ``;
      }

      switch(this.productVariants[0].type) {
        case EProductVariantType.COLOR:
          return this.$t(`products-ufe:variantSelection.color`);
        case EProductVariantType.SIZE:
          return this.$t(`products-ufe:variantSelection.size`);
        default:
          return ``;
      }
    },
    shoppingAllowed(): boolean {
      return !this.limiting.active || (this.limiting.active && this.limiting.shoppingAllowed);
    },
    showProductInformationLink(): boolean {
      return this.productDetails.categories?.find((category): boolean => category.primary)?.infoColorNeeded ?? false;
    },
  },
  props: {
    availableCarts: {
      required: true,
      type: Array as () => IShoppingCartIndividual[],
    },
    isPartner: {
      required: true,
      type: Boolean,
    },
    limiting: {
      required: true,
      type: Object as () => ILimitingObject,
    },
    productDetails: {
      required: true,
      type: Object as () => IProductDetails,
    },
    productInfo: {
      required: true,
      type: Object as () => IProduct,
    },
    productVariants: {
      required: true,
      type: Array as () => IProductVariant[],
    },
    selectedCartId: {
      required: true,
      type: Number,
    },
    teaserDots: {
      required: true,
      type: Array as () => ITeaserDot[],
    },
    unitPriceData: {
      required: true,
      type: Object as () => IUnitPriceData,
    },
  },
  async mounted(): Promise<void> {
    await this.generatePriceInfo();
  },
  methods: {
    async addItemToCart(): Promise<void> {
      const maxAvailable = CartHelper.getMaxAvailableForCart(this.selectedCartId, this.productInfo.matchcode, 99);
      if(this.selectedAmount > maxAvailable) {
        throw new ApplicationError(this.$t(`errors:${EErrorMessage.PRODUCT_AMOUNT_EXCEEDS_MAX}`));
      }
      const selectedCart = this.availableCarts.find(cart => cart.cartId === this.selectedCartId);
      if(selectedCart && (selectedCart.isSingleShipmentCart || (this.productInfo.singleShipmentOnly && selectedCart.items?.length > 0))) {
        throw new ApplicationError(this.$t(`errors:${EErrorMessage.SINGLE_SHIPMENT_PRODUCT}`));
      }
      this.loading = true;
      try {
        const merchandiseItem: IMerchandiseItem | null = await this.$cartService.addItemToCartWithProductInfo<IMerchandiseItem>(
          this.productInfo,
          this.isPartner,
          this.selectedAmount,
          this.selectedCartId,
        );
        if(merchandiseItem) {
          this.$emit(`open:merchandise-overlay`, merchandiseItem);
        }
        this.loading = false;
        this.addedToCart = true;
        setTimeout(() => {
          this.addedToCart = false;
        }, 2000);
      } catch (err) {
        this.loading = false;
        if(!(err instanceof FetchError && err.errorCode === ErrorCodes.LIMITING_BOUNCER_ACTIVE)) {
          throw err;
        }
        // all good, hodor should be active now
      }
    },
    async generatePriceInfo(): Promise<void> {
      const hasNoPriceInfo = !this.productDetails.amount
        || !this.productDetails.unit
        || !this.productDetails.basePrice.brutto
        || !this.productDetails.basePrice.amount;
      this.basePriceInfoText = hasNoPriceInfo
        ? ``
        : `${this.productDetails.amount} ${this.$t(this.productDetails.unit)} (${await this.getPriceString(this.productDetails.basePrice.brutto)} / ${this.productDetails.basePrice.amount} ${this.$t(this.productDetails.unit)})`;
      if(this.productDetails.basePrice.brutto && this.unitPriceData.amount !== null && this.unitPriceData.type !== null) {
        const { text, unitPriceType } = ProductHelper.formatAdditionalUnitPrice(this.productInfo, this.unitPriceData, this.isPartner);
        this.additionalUnitPriceData = text.replaceAll(`{{unitPriceType}}`, this.$t(unitPriceType));
      }
    },
    async getPriceString(price: number | null): Promise<string> {
      if(!this.$currencyService.hasCurrencies()) {
        await this.$currencyService.fetchCurrencies();
      }
      const currencySymbol = this.$currencyService.getCurrencySymbol(this.productInfo.currency);
      return `${currencySymbol} ${FormatHelper.formatPrice(price)}`;
    },
  },
});
